export type FrameworkKey =
  | 'inputs'
  | 'interpretation'
  | 'decisions'
  | 'feedback'
  | 'improvements'
  | 'operating'

export interface FrameworkComponent {
  key: FrameworkKey
  name: string
  color: string
  short: string
  long: string
}

export const FRAMEWORK: FrameworkComponent[] = [
  {
    key: 'inputs',
    name: 'Inputs',
    color: '#818182',
    short: 'The data, mapping, drilling and observations geology runs on.',
    long: 'Data quality, mapping, drilling, sampling, observations, plans, assumptions and context. If inputs are unclear, everything downstream inherits the uncertainty.',
  },
  {
    key: 'interpretation',
    name: 'Interpretation',
    color: '#d07a46',
    short: 'Turning inputs into geological understanding.',
    long: 'Geological thinking, uncertainty, model updates, structures, domains and orebody understanding. Interpretation needs structure, not just instinct.',
  },
  {
    key: 'decisions',
    name: 'Decisions',
    color: '#c4912c',
    short: 'Acting on interpretation under real pressure.',
    long: 'Operational decisions, grade control, mining priorities, risk and communication. Decisions are only as good as the inputs and interpretation behind them.',
  },
  {
    key: 'feedback',
    name: 'Feedback',
    color: '#2a4b66',
    short: 'Comparing what happened to what we expected.',
    long: 'Learning loops, reconciliation, actuals vs expected and lessons from outcomes. Without feedback, the same mistakes repeat across the orebody.',
  },
  {
    key: 'improvements',
    name: 'Improvements',
    color: '#698559',
    short: 'Closing the loop so the system gets better.',
    long: 'Closing the loop, updating standards, improving workflows, training and review rhythms. Improvement must be deliberate, not accidental.',
  },
  {
    key: 'operating',
    name: 'Operating Layer',
    color: '#6f6259',
    short: 'People, processes, standards and consistency.',
    long: 'The layer that holds the cycle together: people, processes, standards and consistency. Systems are built by people, not documents alone.',
  },
]

export interface ProblemCard {
  title: string
  body: string
  relatedColor: string
  related: string
}

export const PROBLEMS: ProblemCard[] = [
  {
    title: 'Inconsistent or biased data',
    body: 'Inputs that drift between people and shifts quietly undermine everything built on top of them.',
    relatedColor: '#818182',
    related: 'Inputs',
  },
  {
    title: 'Assumptions not adequately communicated or understood',
    body: "Interpretation carries assumptions — when they stay in one geologist's head, the system inherits the risk.",
    relatedColor: '#d07a46',
    related: 'Interpretation',
  },
  {
    title: 'Geology disconnected from operational decisions',
    body: 'When geology and operations run on separate tracks, value is lost at the interface.',
    relatedColor: '#c4912c',
    related: 'Decisions',
  },
  {
    title: "Reconciliation that doesn't drive change",
    body: 'Feedback that never reaches the people making the calls is just reporting.',
    relatedColor: '#2a4b66',
    related: 'Feedback',
  },
  {
    title: 'Recurring problems',
    body: "The same issues resurfacing campaign after campaign are a sign improvement loops aren't closing.",
    relatedColor: '#698559',
    related: 'Improvements',
  },
  {
    title: 'Unclear ownership, poor communication and operational culture',
    body: 'Even technically strong systems fail when the operating layer underneath them is weak.',
    relatedColor: '#6f6259',
    related: 'Operating Layer',
  },
]

export type ResourceStatus = 'Free download' | 'Coming soon' | 'Framework tool'

export type ResourceType =
  | 'Guide'
  | 'Checklist'
  | 'Template'
  | 'Prompt sheet'
  | 'Self-assessment'

export interface ResourceItem {
  title: string
  description: string
  category: string
  categoryColor: string
  status: ResourceStatus
  type: ResourceType
}

export const RESOURCES: ResourceItem[] = [
  {
    title: 'MGS System Overview',
    description: 'A complete system overview of each of the areas involved.',
    category: 'Framework',
    categoryColor: '#6f6259',
    status: 'Free download',
    type: 'Guide',
  },
  {
    title: 'MGS Checklist',
    description:
      'A practical review tool designed to assess the health of mine geology systems through structured questions focused on inputs, interpretation, decisions, feedback, improvement and the operating layer. Designed to be completed monthly.',
    category: 'Checklists',
    categoryColor: '#818182',
    status: 'Free download',
    type: 'Checklist',
  },
  {
    title: 'MGS Monthly System Review',
    description:
      'A structured monthly review template used to capture key issues, identify where the system broke down, document actions taken, review outcomes and record lessons learned.',
    category: 'Feedback',
    categoryColor: '#2a4b66',
    status: 'Free download',
    type: 'Template',
  },
  {
    title: 'Yearly Scorecard Tracker',
    description:
      'A yearly tracking tool designed to aggregate monthly checklist results, visualise trends, identify recurring system weaknesses and monitor continuous improvement across mine geology systems over time.',
    category: 'Improvements',
    categoryColor: '#698559',
    status: 'Free download',
    type: 'Template',
  },
  {
    title: 'Mine Geology Systems Starter Guide',
    description:
      'The full framework explained — inputs to continuous improvement — in one practical guide.',
    category: 'Framework',
    categoryColor: '#6f6259',
    status: 'Free download',
    type: 'Guide',
  },
  {
    title: 'Inputs Quality Checklist',
    description:
      'A practical checklist to pressure-test the data and observations feeding your decisions.',
    category: 'Inputs',
    categoryColor: '#818182',
    status: 'Framework tool',
    type: 'Checklist',
  },
  {
    title: 'Interpretation Review Prompt Sheet',
    description:
      'Structured prompts to challenge assumptions before a model update goes live.',
    category: 'Interpretation',
    categoryColor: '#d07a46',
    status: 'Coming soon',
    type: 'Prompt sheet',
  },
  {
    title: 'Decisions & Feedback Loop Checklist',
    description:
      'Make the link between a decision and its outcome explicit — every time.',
    category: 'Feedback',
    categoryColor: '#2a4b66',
    status: 'Coming soon',
    type: 'Checklist',
  },
  {
    title: 'Continuous Improvement Register Template',
    description:
      'A simple register to capture lessons and actually close the loop on them.',
    category: 'Improvements',
    categoryColor: '#698559',
    status: 'Coming soon',
    type: 'Template',
  },
  {
    title: 'Mine Geology Systems Self-Assessment',
    description:
      'Score your site against each part of the framework and find the weakest link first.',
    category: 'Framework',
    categoryColor: '#6f6259',
    status: 'Coming soon',
    type: 'Self-assessment',
  },
  {
    title: 'Site Geology Meeting Rhythm Template',
    description:
      'A simple meeting cadence that keeps feedback and improvement moving every week.',
    category: 'Improvements',
    categoryColor: '#698559',
    status: 'Coming soon',
    type: 'Template',
  },
  {
    title: 'Case Study Template for Mine Geology Problems',
    description:
      'Capture a site problem in framework language so the lesson is reusable, not anecdotal.',
    category: 'Case Studies',
    categoryColor: '#c4912c',
    status: 'Coming soon',
    type: 'Template',
  },
]

export const RESOURCE_FILTERS = [
  'All',
  'Framework',
  'Inputs',
  'Interpretation',
  'Decisions',
  'Feedback',
  'Improvements',
  'Templates',
  'Checklists',
  'Case Studies',
]

export interface InsightTheme {
  title: string
  category: string
  categoryColor: string
  summary: string
  readTime: string
  status: 'Draft article' | 'Coming soon'
}

export const FEATURED_INSIGHT: InsightTheme = {
  title: 'Why mine geology problems are often system problems',
  category: 'Operating Layer',
  categoryColor: '#6f6259',
  summary:
    'The rocks rarely fail you — the system around them does. How unclear inputs, drifting interpretation and missing feedback compound into "geology problems" that geology alone cannot fix.',
  readTime: '~8 min read',
  status: 'Draft article',
}

export const INSIGHTS: InsightTheme[] = [
  {
    title: 'The hidden cost of weak feedback loops in geology teams',
    category: 'Feedback',
    categoryColor: '#2a4b66',
    summary:
      'Every uncaptured lesson is paid for twice — once when it happens, and again when it repeats.',
    readTime: '~6 min read',
    status: 'Draft article',
  },
  {
    title: 'What good geology inputs need before decisions are made',
    category: 'Inputs',
    categoryColor: '#818182',
    summary:
      'Data quality is not just QAQC — it is context, timing and fitness for the decision at hand.',
    readTime: '~6 min read',
    status: 'Draft article',
  },
  {
    title: 'How interpretation changes should flow back into standards',
    category: 'Interpretation',
    categoryColor: '#d07a46',
    summary:
      'A model update that never changes a standard is a lesson the system did not learn.',
    readTime: '~7 min read',
    status: 'Draft article',
  },
  {
    title: 'Why continuous improvement needs a rhythm, not just intention',
    category: 'Improvements',
    categoryColor: '#698559',
    summary:
      'Improvement is a cadence, not a one-off workshop after something goes wrong.',
    readTime: '~5 min read',
    status: 'Draft article',
  },
  {
    title: 'When grade control inputs do not match decision needs',
    category: 'Case Studies',
    categoryColor: '#c4912c',
    summary:
      'Good data collected for the wrong question still leaves decisions exposed.',
    readTime: '~6 min read',
    status: 'Coming soon',
  },
  {
    title: 'When interpretation changes are not captured in the feedback loop',
    category: 'Case Studies',
    categoryColor: '#c4912c',
    summary:
      'Model updates that never reach reconciliation quietly cost you twice.',
    readTime: '~7 min read',
    status: 'Coming soon',
  },
  {
    title: 'When standards exist but consistency breaks down',
    category: 'Case Studies',
    categoryColor: '#c4912c',
    summary:
      'The gap between the written standard and the bench is where systems fail.',
    readTime: '~5 min read',
    status: 'Coming soon',
  },
  {
    title: 'When geology teams need a better improvement rhythm',
    category: 'Case Studies',
    categoryColor: '#c4912c',
    summary:
      'Improvement is a cadence, not a one-off workshop after something goes wrong.',
    readTime: '~6 min read',
    status: 'Coming soon',
  },
]

export const INSIGHT_FILTERS = [
  'All',
  'Inputs',
  'Interpretation',
  'Decisions',
  'Feedback',
  'Improvements',
  'Operating Layer',
  'Case Studies',
]

export const NAV_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Framework', href: '/framework' },
  { label: 'Resources', href: '/resources' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact', href: '/contact' },
]

export const ROLE_OPTIONS = [
  'Junior geologist',
  'Mine geologist',
  'Senior geologist',
  'Geology superintendent',
  'Technical services',
  'Other',
]

export const LOGO_DARK =
  'https://cdn.magicpatterns.com/uploads/9rYFXm5knHXLG9aZijYk1X/MGS_logo-Main-dark.png'
export const WHEEL_IMG =
  'https://cdn.magicpatterns.com/uploads/3BnyPDogvJiq7GFXJr5pLp/System_graphic.png'

export interface FaqItem {
  q: string
  a: string
}

export const FAQS: FaqItem[] = [
  {
    q: 'Is this just another sales funnel?',
    a: 'No. MGS is an audience-first project. The guide, checklists and case studies are genuinely free and useful. You can take everything and never speak to Jordan — that is the point. Consulting, workshops and reviews may come later, but right now this is about building better systems and a community of geologists who care.',
  },
  {
    q: 'Is this for junior geologists or senior leaders?',
    a: 'Both. Juniors get a clear way to think about the work beyond their immediate tasks. Seniors and superintendents get a shared language and structure to lift the whole team. The framework scales from one geologist to a full technical services group.',
  },
  {
    q: 'Will this actually help on my site?',
    a: 'The framework is deliberately practical and tool-agnostic — it works whether you run open pit gold, underground base metals or anything in between. It is not software to install; it is a way to find where your geology system leaks value and fix it.',
  },
  {
    q: 'What happens after I download the guide?',
    a: 'You get the guide straight away, plus occasional Field Notes — new resources, case studies and framework thinking, sent only when there is something worth reading. No drip sequence, no spam, unsubscribe in one click.',
  },
  {
    q: 'Is MGS a software product?',
    a: 'No. MGS is a thinking framework and a resource library. It sits above whatever tools and databases you already use, helping your people, processes and standards work together more consistently.',
  },
  {
    q: 'Who is behind this?',
    a: 'Mine Geology Systems is built by Jordan Biglin, a working mine geologist drawing on real field experience across mine geology systems. Everything here comes from the practical reality of doing the work, not theory.',
  },
]

export const TAGLINE = 'Better Systems – Better Decisions – Better Outcomes'

export const CONTACT_EMAIL = 'hello@minegeologysystems.com'

export const LINKEDIN_LABEL = 'Mine Geology Systems / Jordan Biglin'

export const DISCLAIMER =
  'Mine Geology Systems (MGS) provides general operational frameworks, tools and educational content intended to support structured thinking and continuous improvement in mine geology systems. MGS content is not site-specific technical advice and should not replace professional judgement, operational procedures, or company standards. Users remain responsible for verifying the suitability and application of any information within their own operational context.'
